import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { uniqBy } from "lodash";
import i18next from "../../common/i18n";
import { db, enums, http } from "../../common/lib";
import { actionCreators, actionTypes } from "./actions";
import { actionCreators as toastActions } from "../toast/actions";
import { get, update } from "idb-keyval";
import config from "../../config";
import deviceDownload from "./deviceDownload";
import actions from "../actions";
import { logToSentry } from "../../config/Sentry";

const format = (data: Array<any>) => {
    const altAssets = [];
    const auditAssets = [];
    const auditAssignments = [];
    const audits = [];

    for (let d of data) {
        const { auditLocation } = d;
        const { audit } = auditLocation;

        altAssets.push(...d.altAssets.map((item) => ({ ...item, audit })));
        auditAssets.push(
            ...d.auditAssets.map((item) => ({
                ...item,
                auditLocation,
                deferredTimestamp: 0,
            }))
        );

        auditAssignments.push({ ...d.auditAssignment, auditLocation });
        audits.push(auditLocation.audit);
    }
    return [altAssets, auditAssets, auditAssignments, uniqBy(audits, "id")];
};

function* post() {
    const localState = yield select((state) => state);
    let pendingSubmissionDB =
        (yield call(get, db.types.pendingSubmission)) || [];

    const { getDeviceInfo } = deviceDownload;

    const internalData = [];
    localState.auditAssignments.data.forEach((assignment) => {
        const found = pendingSubmissionDB.find(
            (item) => item.assignmentId === assignment.id
        );
        internalData.push({
            assignmentId: assignment.id,
            isSubmitted: !found,
        });
    });

    try {
        const { data } = yield call(
            http.authorized.post,
            config.api.paths.auditDownload,
            {
                downloadInformation: getDeviceInfo(),
                internalData,
            }
        );

        const [altAssets, auditAssets, auditAssignments, audits] = format(data);

        if (auditAssignments.length > 0) {
            yield call(
                http.authorized.post,
                config.api.paths.auditAssignmentBulkUpdate,
                auditAssignments.map((item) => ({
                    id: item.id,
                    status: enums.auditAssignment.pendingSubmission,
                }))
            );
        }

        // UPDATE AltAssets IDB
        if (altAssets.length > 0)
            yield call(update, db.types.altAsset, (altAssetsDB = []) => {
                if (!altAssetsDB) altAssetsDB = [];
                return [...altAssetsDB, ...altAssets];
            });

        // UPDATE AuditAssets IDB
        if (auditAssets.length > 0) {
            yield call(update, db.types.auditAsset, (auditAssetsDB = []) => {
                if (!auditAssetsDB) auditAssetsDB = [];
                /**
                 * To avoid assets duplication we need to check if same assets,
                 * already exist in IDB and avoid storing them
                 */
                const newAssets = auditAssets.filter((asset) => {
                    let assetId = asset.assetCore.id;
                    let auditId = asset.auditLocation.audit.id;
                    let locationId = asset.auditLocation.location.id;
                    /**
                     * Controller issues 2 audits 1 gets submitted and other stays on PWA
                     * When there is one audit present in pwa and previous audit/asset was reissued
                     * If the reissued asset/audit still exists in DB
                     * change the reissued status to false
                     * Reissued can happen for audit or asset(s)
                     */
                    if (auditAssetsDB) {
                        const foundAsset = auditAssetsDB.find((item) => {
                            if (
                                item.assetCore.id === assetId &&
                                item.auditLocation.audit.id === auditId &&
                                item.auditLocation.location.id === locationId
                            ) {
                                //If asset is reissued mark isAssigned  false
                                if (asset.reissuedStatus) {
                                    item.isAssigned = false;
                                }
                                return true;
                            }
                        });
                        // if not, we need to add it
                        if (!foundAsset) return true;
                    }

                    return false;
                });

                return [...auditAssetsDB, ...newAssets];
            });
        }
        const assignmentDownloadedStatus = auditAssignments.map((item) => {
            return {
                assignmentId: item.id,
                isDownloaded: item.isDownloaded,
            };
        });

        // UPDATE AuditAssignments IDB
        if (auditAssignments.length > 0) {
            yield call(
                update,
                db.types.auditAssignment,
                (auditAssignmentsDB = []) => {
                    if (!auditAssignmentsDB) auditAssignmentsDB = [];
                    /**
                     * On audit reissue, assignment gets a new id,
                     * we need to remap old assignment.id with new one that belong to this audit
                     */

                    const updatedAssignments = auditAssignmentsDB.filter(
                        (assignment) => {
                            let locationId =
                                assignment.auditLocation.location.id;
                            let auditId = assignment.auditLocation.audit.id;
                            let auditorId = assignment.auditor;

                            const foundAssignment = auditAssignments.find(
                                (item) =>
                                    item.auditor === auditorId &&
                                    item.auditLocation.audit.id === auditId &&
                                    item.auditLocation.location.id ===
                                        locationId
                            );

                            // dont include assignment
                            if (foundAssignment) return false;

                            return true;
                        }
                    );

                    return [...updatedAssignments, ...auditAssignments];
                }
            );

            // create independ of auditAssigment records that wil indicate audit submission
            const pendingSubmission = auditAssignments.map((item) => {
                return {
                    auditId: item.auditLocation.audit.id,
                    assignmentId: item.id,
                };
            });

            yield call(
                update,
                db.types.pendingSubmission,
                (pendingSubmissionDB = []) => {
                    if (!pendingSubmissionDB) pendingSubmissionDB = [];
                    return [...pendingSubmissionDB, ...pendingSubmission];
                }
            );
        }

        yield put(
            actionCreators.postSuccess(audits, assignmentDownloadedStatus)
        );
    } catch (error) {
        const errStr = error.toString();
        logToSentry("audit download post", {
            error,
            errStr,
        });
        yield all([
            put(actionCreators.postFailure(errStr)),
            put(toastActions.error(i18next.t("errors.auditDownloadGet"))),
        ]);
    }
}

function* cleanupAudits(action) {
    const { auditId } = action.payload;
    try {
        /** Remove alt Assets */
        yield call(update, db.types.altAsset, (updatedAltAssetDB = []) => {
            if (!updatedAltAssetDB) updatedAltAssetDB = [];
            const updatedAssets = updatedAltAssetDB.filter(
                (obj) => obj.audit.id !== auditId
            );
            return updatedAssets;
        });

        // /** Remove Asset Auditor */
        yield call(
            update,
            db.types.assetAuditor,
            (updatedAssetAuditorDB = []) => {
                if (!updatedAssetAuditorDB) updatedAssetAuditorDB = [];
                const updatedAssets = updatedAssetAuditorDB.filter(
                    (obj) =>
                        obj.auditAssignment.auditLocation.audit.id !== auditId
                );
                return updatedAssets;
            }
        );

        // /** Remove Audit Asset */
        yield call(update, db.types.auditAsset, (updatedAuditAssetDB = []) => {
            if (!updatedAuditAssetDB) updatedAuditAssetDB = [];
            const updatedAssets = updatedAuditAssetDB.filter(
                (obj) => obj.auditLocation.audit.id !== auditId
            );
            return updatedAssets;
        });

        // /** Remove Audit Assignemt */
        yield call(
            update,
            db.types.auditAssignment,
            (updatedAuditAssignmentDB = []) => {
                if (!updatedAuditAssignmentDB) updatedAuditAssignmentDB = [];
                const updatedAssets = updatedAuditAssignmentDB.filter(
                    (obj) => obj.auditLocation.audit.id !== auditId
                );
                return updatedAssets;
            }
        );

        // /** Remove Pending Submission DB */
        yield call(
            update,
            db.types.pendingSubmission,
            (updatedPendingSubmissionDB = []) => {
                if (!updatedPendingSubmissionDB)
                    updatedPendingSubmissionDB = [];
                const updatedAssets = updatedPendingSubmissionDB.filter(
                    (obj) => obj.auditId !== auditId
                );
                return updatedAssets;
            }
        );

        /** Update the asset record for that audit */
        yield put(actionCreators.cleanupSuccess());
    } catch (error) {
        console.log("error", error);
        const errStr = error.toString();
        logToSentry("clean up audits", {
            error,
            errStr,
            actionPayload: action.payload,
        });
        yield put(actionCreators.cleanupFailure(errStr));
    }
}

export { post };

export default (function* () {
    yield all([
        takeLatest(actionTypes.AUDIT_DOWNLOAD_POST_REQUEST, post),
        takeLatest(actionTypes.AUDITS_CLEANUP_POST_REQUEST, cleanupAudits),
    ]);
})();
